"use client";

export interface BulkProgressIndicatorProps {
  currentIndex: number;
  totalItems: number;
  /** Listings that pass validation (rules + required fields + images). */
  readyCount: number;
  className?: string;
}

export default function BulkProgressIndicator({
  currentIndex,
  totalItems,
  readyCount,
  className = "",
}: BulkProgressIndicatorProps) {
  const safeTotal = Math.max(1, totalItems);
  const displayIndex = Math.min(currentIndex + 1, safeTotal);
  const pctReady =
    safeTotal > 0 ? Math.round((readyCount / safeTotal) * 100) : 0;

  return (
    <div
      className={`rounded-xl border border-border bg-surface p-4 shadow-sm ${className}`}
    >
      <div className="flex gap-3 flex-col gap-6">
        <div>
          <p
            className="text-base font-semibold text-foreground"
            aria-live="polite"
          >
            Editing listing {displayIndex} of {safeTotal}
          </p>
          <p className="text-sm text-text-light mt-0.5">
            {readyCount === safeTotal ? (
              <span className="text-primary-700 font-medium">
                All {safeTotal} listing{safeTotal === 1 ? "" : "s"} ready to
                submit
              </span>
            ) : (
              <>
                <span className="font-medium text-foreground">
                  {readyCount}
                </span>{" "}
                of {safeTotal} complete — finish the rest or review to see
                what&apos;s missing
              </>
            )}
          </p>
        </div>
        <div className="w-full sm:w-40 shrink-0">
          <div
            className="h-2 rounded-full bg-secondary overflow-hidden"
            role="progressbar"
            aria-valuenow={readyCount}
            aria-valuemin={0}
            aria-valuemax={safeTotal}
            aria-label="Listings ready to submit"
          >
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
              style={{ width: `${pctReady}%` }}
            />
          </div>
          <p className="text-xs text-text-light mt-1 text-right sm:text-left">
            {pctReady}% ready
          </p>
        </div>
      </div>
    </div>
  );
}
